import { Controller } from '@hotwired/stimulus';
import { loadSearchIndex, scheduleSearchWarmup } from '../search/load-index.js';

class ResultRow {
  constructor(fragment) {
    this.fragment = fragment;
    this.link = findTarget(fragment, 'resultLink');
    this.title = findTarget(fragment, 'resultTitle');
    this.description = findTarget(fragment, 'resultDescription');
    this.tags = findTarget(fragment, 'resultTags');
  }

  static create(template) {
    const fragment = cloneTemplate(template);
    return new ResultRow(fragment);
  }
}

class ResultTag {
  constructor(fragment) {
    this.fragment = fragment;
    this.tag = findTarget(fragment, 'tagItem');
  }

  static create(template) {
    const fragment = cloneTemplate(template);
    return new ResultTag(fragment);
  }
}

class LoadingRow {
  constructor(fragment) {
    this.fragment = fragment;
    this.element = fragment.firstElementChild;
    this.label = findTarget(fragment, 'loadingLabel');
  }

  static create(template) {
    const fragment = cloneTemplate(template);
    return new LoadingRow(fragment);
  }
}

class EmptyRow {
  constructor(fragment) {
    this.fragment = fragment;
    this.element = fragment.firstElementChild;
    this.label = findTarget(fragment, 'emptyLabel');
  }

  static create(template) {
    const fragment = cloneTemplate(template);
    return new EmptyRow(fragment);
  }
}

function cloneTemplate(template) {
  const fragment = template.content.cloneNode(true);

  if (!fragment.firstElementChild) {
    throw new Error('Template must contain a root element');
  }

  return fragment;
}

function findTarget(fragment, targetName) {
  const selector = `[data-search-target~="${targetName}"]`;
  const root = fragment.firstElementChild;

  if (root && root.matches(selector)) {
    return root;
  }

  const node = fragment.querySelector(selector);

  if (!node) {
    throw new Error(`Template missing target ${targetName}`);
  }

  return node;
}

function isAbortError(error) {
  return error && (error.name === 'AbortError' || error.code === 20);
}

// Keep the search UI responsive while its FlexSearch engine warms in the background.
export default class extends Controller {
  static targets = [
    'input',
    'results',
    'template',
    'tagTemplate',
    'loadingTemplate',
    'emptyTemplate',
    'resultLink',
    'resultTitle',
    'resultDescription',
    'resultTags',
    'tagItem',
    'loadingLabel',
    'emptyLabel'
  ];

  static values = {
    indexUrl: String,
    engineUrl: String,
    pageSuffix: { type: String, default: 'index.json' },
    minLength: { type: Number, default: 1 },
    debug: { type: Boolean, default: false }
  };

  // Stimulus lifecycle & actions
  connect() {
    this.term = '';
    this.index = null;
    this.pages = [];
    this.rawResults = [];
    this.latestRequestedTerm = null;
    this.currentSearchAbort = null;
    this.currentSearchPromise = null;

    ResultRow.create(this.templateTarget);
    ResultTag.create(this.tagTemplateTarget);
    LoadingRow.create(this.loadingTemplateTarget);
    EmptyRow.create(this.emptyTemplateTarget);

    this._hideResults();

    this._debug('connect: controller initialized');

    this.cancelWarmup = scheduleSearchWarmup(() => {
      void this._loadIndex('low').catch((error) => {
        console.error('Failed to preload search index', error);
      });
    });
  }

  disconnect() {
    this.cancelWarmup?.();
    this.cancelWarmup = null;
    this._abortCurrentSearch();
  }

  prepare() {
    void this._loadIndex().catch((error) => {
      console.error('Failed to prepare search index', error);
    });
  }

  async updateTerm(event) {
    this.term = event.target.value || '';
    this._debug('updateTerm', { term: this.term });

    if (!this.term.length) {
      this._reset({ keepInput: true });
      return;
    }

    if (this.term.length < this.minLengthValue) {
      this.pages = [];
      this._abortCurrentSearch();
      this._hideResults();
      this._debug('updateTerm: below minLength, hiding results');
      return;
    }

    await this._startSearch(this.term);
  }

  async submit(event) {
    event.preventDefault();

    const term = this.inputTarget.value || '';
    this.term = term;
    this._debug('submit', { term });

    if (!term.length) {
      this._reset();
      return;
    }

    if (term.length < this.minLengthValue) {
      return;
    }

    await this._startSearch(term);
  }

  // Internal workflow helpers
  async _startSearch(term) {
    this._abortCurrentSearch();

    const abortController = new AbortController();

    if (this.currentSearchAbort) {
      throw new Error('Attempted to start a search while another is running');
    }

    this.currentSearchAbort = abortController;
    this.latestRequestedTerm = term;
    this._debug('_startSearch: begin', { term });
    this._showLoadingIndicator();

    const run = async () => {
      try {
        await this._performSearch(term, abortController.signal);
      } catch (error) {
        if (isAbortError(error) || abortController.signal.aborted || this.latestRequestedTerm !== term) {
          this._debug('_startSearch: aborted', { term });
          return;
        }

        console.error('Search failed', error);
        this.pages = [];
        this._renderResults();
      } finally {
        if (this.currentSearchAbort === abortController) {
          this.currentSearchAbort = null;
        }
        if (this.currentSearchPromise === currentPromise) {
          this.currentSearchPromise = null;
        }
        this._debug('_startSearch: finished', { term, pages: this.pages.length });
      }
    };

    const currentPromise = run();
    this.currentSearchPromise = currentPromise;
    await currentPromise;
  }

  async _performSearch(term, signal) {
    const index = await this._loadIndex();
    if (signal.aborted) return;
    const rawResults = await index.search(term, { limit: 50 });
    if (signal.aborted) return;

    this.rawResults = rawResults;
    this._debug('_performSearch: flexsearch results', { term, fields: rawResults.length });

    const refs = [];
    const seen = new Set();

    for (const fieldResult of rawResults) {
      if (!fieldResult || !Array.isArray(fieldResult.result)) continue;
      for (const ref of fieldResult.result) {
        if (!seen.has(ref)) {
          seen.add(ref);
          refs.push(ref);
        }
      }
    }

    const pages = await Promise.all(
      refs.map(async (ref) => {
        try {
          this._debug('_performSearch: fetch page', { ref });
          return await this._fetchPageData(ref, signal);
        } catch (error) {
          if (isAbortError(error)) {
            throw error;
          }
          console.error('Failed to fetch page data for search result', error);
          return null;
        }
      })
    );

    if (signal.aborted || this.latestRequestedTerm !== term) {
      return;
    }

    this.pages = pages.filter(Boolean);
    this._debug('_performSearch: pages ready', { count: this.pages.length });
    this._renderResults();
  }

  async _loadIndex(priority = 'auto') {
    this.cancelWarmup?.();
    this.cancelWarmup = null;
    if (this.index) {
      this._debug('_loadIndex: using cached index');
      return this.index;
    }

    this.index = await loadSearchIndex(this.indexUrlValue || '/index-flexsearch.json', priority, this.engineUrlValue);
    return this.index;
  }

  async _fetchPageData(ref, signal) {
    const suffix = this.pageSuffixValue || 'index.json';
    const url = `${ref}${suffix}`;

    const options = {};
    if (signal) {
      options.signal = signal;
    }

    this._debug('_fetchPageData: start', { url });

    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`Failed to fetch page data for ${ref} (${response.status})`);
    }

    const data = await response.json();
    this._debug('_fetchPageData: done', { url });
    return data;
  }

  _renderResults() {
    const results = this.resultsTarget;
    results.replaceChildren();

    if (!this.pages.length) {
      this._showResults();
      const emptyRow = EmptyRow.create(this.emptyTemplateTarget);
      results.appendChild(emptyRow.fragment);
      this._hideLoadingIndicator();
      this._debug('_renderResults: empty');
      return;
    }

    this._showResults();

    const assembled = document.createDocumentFragment();

    for (const page of this.pages) {
      const row = ResultRow.create(this.templateTarget);

      row.link.href = page.uri;
      row.link.setAttribute('aria-current', 'true');

      row.title.textContent = page.title || '';
      row.description.textContent = page.description || '';

      row.tags.replaceChildren();

      const tags = Array.isArray(page.tags) ? page.tags : [];

      for (const tag of tags) {
        const tagRow = ResultTag.create(this.tagTemplateTarget);
        tagRow.tag.textContent = `${tag}\u00a0`;
        row.tags.appendChild(tagRow.fragment);
      }

      assembled.appendChild(row.fragment);
    }

    results.replaceChildren(assembled);
    this._hideLoadingIndicator();
    this._debug('_renderResults: rendered', { count: this.pages.length });
  }

  _reset({ keepInput = false } = {}) {
    if (!keepInput) {
      this.inputTarget.value = '';
    }

    this.term = '';
    this.pages = [];
    this.rawResults = [];
    this.latestRequestedTerm = null;
    this._abortCurrentSearch();
    this._hideResults();
    this._debug('_reset: done', { keepInput });
  }

  _showResults() {
    this.resultsTarget.removeAttribute('hidden');
    this.resultsTarget.classList.remove('is-empty');
    this._debug('_showResults');
  }

  _hideResults() {
    this._hideLoadingIndicator();
    this.resultsTarget.setAttribute('hidden', 'true');
    this.resultsTarget.classList.add('is-empty');
    this.resultsTarget.replaceChildren();
    this._debug('_hideResults');
  }

  _showLoadingIndicator() {
    if (this.term.length < this.minLengthValue) {
      return;
    }

    const container = this.resultsTarget;

    container.setAttribute('aria-busy', 'true');
    this._showResults();

    const loadingRow = LoadingRow.create(this.loadingTemplateTarget);
    container.replaceChildren(loadingRow.fragment);

    this._debug('_showLoadingIndicator', { term: this.term });
  }

  _hideLoadingIndicator() {
    this.resultsTarget.removeAttribute('aria-busy');
  }

  _abortCurrentSearch() {
    if (this.currentSearchAbort) {
      this.currentSearchAbort.abort();
      this.currentSearchAbort = null;
      this._debug('_abortCurrentSearch: aborted running search');
    }

    this.currentSearchPromise = null;
    this.latestRequestedTerm = null;
    this._hideLoadingIndicator();
  }


  _debug(message, payload) {
    if (!this.debugValue) {
      return;
    }

    if (payload === undefined) {
      console.debug('[search]', message);
    } else {
      console.debug('[search]', message, payload);
    }
  }
}
